<template>
  <drawer-section title="Generate with AI" class="widget-ai">
    <template #icon>
      <img src="/img/pandas/biku-bot.png" alt="" class="widget-ai__avatar" />
    </template>
    <template #header-actions>
      <span class="widget-ai__beta">Beta</span>
      <i class="tim-icons icon-simple-remove widget-ai__close" title="Hide" @click="$emit('close')"></i>
    </template>

    <el-input
      type="textarea"
      :rows="2"
      v-model="prompt"
      :disabled="loading"
      resize="none"
      placeholder="Describe the widget, e.g. “Break down how customers heard about us this year”"
      @keydown.native.meta.enter="generate"
      @keydown.native.ctrl.enter="generate" />

    <div class="widget-ai__actions">
      <small v-if="error" class="widget-ai__error">{{ error }}</small>
      <small v-else-if="hint" class="widget-ai__hint">{{ hint }}</small>
      <small v-else-if="loading" class="widget-ai__hint">Biku is working on it. The first request can take a few seconds.</small>
      <small v-else class="widget-ai__hint">Biku fills in the widget below for you to review.</small>
      <base-button type="primary" size="sm" class="widget-ai__btn" :disabled="loading || !prompt.trim()" @click="generate">
        <span v-if="loading"><i class="el-icon-loading"></i> Thinking…</span>
        <span v-else>Generate</span>
      </base-button>
    </div>
  </drawer-section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Auth } from 'aws-amplify'
import axios from 'axios'
import { API_LOCATION } from '@/api-config'

const props = defineProps<{
  dashboardId: string | number
}>()

const emit = defineEmits<{
  (e: 'generated', payload: { config: any; unresolved: string[] }): void
  (e: 'close'): void
}>()

const prompt = ref('')
const loading = ref(false)
const error = ref('')
const hint = ref('')

async function getToken(): Promise<string> {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

async function generate() {
  if (loading.value || !prompt.value.trim()) return
  loading.value = true
  error.value = ''
  hint.value = ''
  try {
    const token = await getToken()
    const companyId = localStorage.getItem('companyId')
    const companyGroupId = JSON.parse(localStorage.getItem('group') as string).id
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/dashboards/${props.dashboardId}/widgets/generate`
    const res = await axios.post(url, { prompt: prompt.value.trim() }, { headers: { Authorization: token } })

    const config = res.data?.config
    const unresolved: string[] = res.data?.unresolved || []
    if (!config) {
      error.value = res.data?.message || 'Could not interpret that. Try rephrasing.'
      return
    }
    if (unresolved.length) {
      hint.value = 'Filled in what I could. Double-check the fields below before saving.'
    }
    emit('generated', { config, unresolved })
  } catch (err: any) {
    // New lambdas return JSON errors; fall back to a plain string.
    let msg = 'Something went wrong. Please try again.'
    const data = err?.response?.data
    if (data) {
      try { msg = (typeof data === 'string' ? JSON.parse(data) : data).error || msg } catch { if (typeof data === 'string') msg = data }
    }
    if (err?.response?.status === 429) msg = 'Daily AI limit reached. Please try again tomorrow.'
    error.value = msg
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.widget-ai__avatar {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-right: 10px;
}
.widget-ai__beta {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.15);
  border: 1px solid rgba(225, 78, 202, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  line-height: 1;
}
.widget-ai__close {
  cursor: pointer;
  font-size: 12px !important;
  color: rgba(125, 125, 125, 0.9) !important;
}
.widget-ai__close:hover {
  color: #e14eca !important;
}
.widget-ai__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}
.widget-ai__btn {
  flex-shrink: 0;
}
.widget-ai__error {
  color: #fd5d93;
}
.widget-ai__hint {
  color: rgba(125, 125, 125, 0.95);
  line-height: 1.3;
}
</style>

<!-- Non-scoped: theme the Element UI textarea for both modes (its default white background
     clashes in dark mode). Keyed on .widget-ai so it stays local to this panel. -->
<style>
.widget-ai .el-textarea__inner {
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.4285rem;
  font-size: 0.875rem;
}
.widget-ai .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.4);
}
.widget-ai .el-textarea__inner:focus {
  border-color: #e14eca;
}
.white-content .widget-ai .el-textarea__inner {
  background: #ffffff;
  color: rgba(0, 0, 0, 0.85);
  border-color: #e3e3e3;
}
.white-content .widget-ai .el-textarea__inner:focus {
  border-color: #e14eca;
}
.white-content .widget-ai .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.4);
}
</style>
